import { useCallback, useId, useRef, useState, type ReactNode, type Ref, type RefObject } from 'react';
import { X } from '@phosphor-icons/react';
import { cx } from '../cx';
import { IconButton } from '../IconButton/IconButton';
import { Portal } from '../Portal';
import { useDismiss } from '../hooks/useDismiss';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useScrollLock } from '../hooks/useScrollLock';

export type SheetSize = 'sm' | 'lg';

export type SheetPanelProps = {
  title: ReactNode;
  children?: ReactNode;
  /** Sticky footer, usually the apply/save buttons. */
  footer?: ReactNode;
  /** Accessible name of the close button, localised by the caller. */
  closeLabel: string;
  onClose?: () => void;
  size?: SheetSize;
  titleId?: string;
  panelRef?: Ref<HTMLDivElement>;
  className?: string;
};

/**
 * The sheet surface on its own (used by Sheet, and by the showcase to draw it open): rounded top
 * with a grabber on phones, a rounded card from md up. The body scrolls; header and footer stay.
 */
export function SheetPanel({
  title,
  children,
  footer,
  closeLabel,
  onClose,
  size = 'sm',
  titleId,
  panelRef,
  className,
}: SheetPanelProps) {
  const ownId = `sp${useId().replace(/:/g, '')}`;
  const headingId = titleId ?? ownId;
  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      style={{ maxHeight: '92dvh' }}
      className={cx(
        'flex w-full flex-col overflow-hidden bg-jc-surface text-jc-ink shadow-jc-lg',
        // Rises from the bottom on phones, pops in from md; reduced motion gets neither.
        'motion-safe:animate-jc-rise md:motion-safe:animate-jc-pop',
        'rounded-t-jc-container md:rounded-jc-container',
        size === 'sm' ? 'md:max-w-120' : 'md:max-w-160',
        className,
      )}
    >
      <div aria-hidden="true" className="flex justify-center pt-2 md:hidden">
        <span className="h-1 w-10 rounded-jc-control bg-jc-border-control" />
      </div>
      <div className="flex items-center gap-3 border-b border-jc-border px-5 py-3 md:px-6 md:py-4">
        <h2 id={headingId} className="min-w-0 flex-1 type-heading text-jc-ink">
          {title}
        </h2>
        <IconButton icon={X} label={closeLabel} variant="ghost" onClick={onClose} className="-mr-2" />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 md:px-6">{children}</div>
      {footer ? (
        <div
          className="flex flex-wrap justify-end gap-2 border-t border-jc-border px-5 py-3 md:px-6"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export type SheetProps = Omit<SheetPanelProps, 'titleId' | 'panelRef' | 'onClose'> & {
  open: boolean;
  onClose: () => void;
  initialFocus?: RefObject<HTMLElement | null>;
};

/**
 * A larger task on top of the page (filters, editing a profile section): a bottom sheet on phones,
 * a centred panel from md. Modal like Dialog: focus trap, scroll lock, Escape and scrim close.
 */
export function Sheet({ open, onClose, initialFocus, ...panel }: SheetProps) {
  const titleId = `sh${useId().replace(/:/g, '')}`;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(false);
  const attach = useCallback((el: HTMLDivElement | null) => {
    panelRef.current = el;
    setMounted(!!el);
  }, []);
  useFocusTrap(panelRef, open && mounted, { initialFocus });
  useScrollLock(open);
  useDismiss({ open, onDismiss: onClose, refs: [panelRef], outsidePress: false });
  if (!open) return null;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-(--jc-z-sheet) flex items-end justify-center bg-jc-scrim motion-safe:animate-jc-fade md:items-center md:p-4"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <SheetPanel {...panel} onClose={onClose} titleId={titleId} panelRef={attach} />
      </div>
    </Portal>
  );
}
